/**
 * ChecklistSuggestions Component
 *
 * Displays suggested checklist item matches for a document with
 * batch accept/reject functionality. Grouped by category with
 * collapsible cards. Reused in both upload sheet and document detail views.
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import {
  CheckSquare,
  Square,
  Loader2,
  ChevronDown,
  BookOpen,
  FileText,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import type { Id } from "@/packages/types";
import {
  useChecklistSuggestions,
  type ResolvedSuggestion,
} from "@/hooks/use-checklist-suggestions";
import { Button } from "@/components/ui/primitives";

interface ChecklistSuggestionsProps {
  documentId: Id<"documents">;
  onComplete?: () => void;
}

interface CategoryGroup {
  categoryId: string;
  categoryName: string;
  suggestions: ResolvedSuggestion[];
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  const colorMap: Record<string, string> = {
    high: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
    medium: "bg-amber-500/15 text-amber-400 border-amber-500/20",
    low: "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  };
  const colors = colorMap[confidence] ?? colorMap.low;

  return (
    <span
      className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${colors}`}
    >
      {confidence}
    </span>
  );
}

function ClauseRefPill({ clause }: { clause: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/15">
      <BookOpen size={10} />
      {clause}
    </span>
  );
}

function SuggestionCard({
  suggestion,
  isSelected,
  onToggle,
}: {
  suggestion: ResolvedSuggestion;
  isSelected: boolean;
  onToggle: () => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasExpandableContent =
    suggestion.relevantExcerpt ||
    suggestion.clauseRefs.length > 0 ||
    suggestion.exampleDocuments.length > 0;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Collapsed row: checkbox + clickable title area */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: The role and handlers are conditionally applied only when content is expandable */}
      <div
        className={`flex items-center gap-2.5 p-3 ${hasExpandableContent ? "cursor-pointer hover:bg-card-hover transition-colors" : ""}`}
        onClick={
          hasExpandableContent ? () => setIsExpanded(!isExpanded) : undefined
        }
        onKeyDown={
          hasExpandableContent
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsExpanded(!isExpanded);
                }
              }
            : undefined
        }
        role={hasExpandableContent ? "button" : undefined}
        tabIndex={hasExpandableContent ? 0 : undefined}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="shrink-0 text-muted hover:text-primary transition-colors cursor-pointer"
        >
          {isSelected ? (
            <CheckSquare size={16} className="text-accent" />
          ) : (
            <Square size={16} />
          )}
        </button>

        <p className="text-[13px] text-primary font-medium truncate flex-1 text-left">
          {suggestion.title}
        </p>

        <ConfidenceBadge confidence={suggestion.confidence} />

        {hasExpandableContent && (
          <ChevronDown
            size={14}
            className={`shrink-0 text-muted transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        )}
      </div>

      {/* Expanded details */}
      <AnimatePresence>
        {isExpanded && hasExpandableContent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-2.5 border-t border-border/50 pt-2.5 ml-[34px]">
              {/* Clause refs */}
              {suggestion.clauseRefs.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {suggestion.clauseRefs.map((clause) => (
                    <ClauseRefPill key={clause} clause={clause} />
                  ))}
                </div>
              )}

              {/* AI reason */}
              <p className="text-[12px] text-secondary leading-relaxed">
                {suggestion.reason}
              </p>

              {/* Example documents */}
              {suggestion.exampleDocuments.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] uppercase text-muted font-medium tracking-wider">
                    Example documents
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestion.exampleDocuments.map((doc) => (
                      <span
                        key={doc.name}
                        className="inline-flex items-center gap-1 text-[11px] text-muted px-1.5 py-0.5 rounded bg-card-hover"
                      >
                        <FileText size={10} />
                        {doc.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Relevant excerpt */}
              {suggestion.relevantExcerpt && (
                <p className="text-[11px] text-muted italic px-2 py-1.5 bg-card-hover rounded">
                  &ldquo;{suggestion.relevantExcerpt}&rdquo;
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function ChecklistSuggestions({
  documentId,
  onComplete,
}: ChecklistSuggestionsProps) {
  const { suggestions, acceptSuggestions, dismissAll, isAccepting } =
    useChecklistSuggestions(documentId);

  const [selectedIds, setSelectedIds] = useState<Set<Id<"checklistItems">>>(
    () => new Set(suggestions.map((s) => s.checklistItemId)),
  );

  // Update selections when suggestions load
  useEffect(() => {
    if (suggestions.length > 0) {
      setSelectedIds(new Set(suggestions.map((s) => s.checklistItemId)));
    }
  }, [suggestions]);

  // Group suggestions by category
  const categoryGroups: CategoryGroup[] = useMemo(() => {
    const groupMap = new Map<string, CategoryGroup>();
    for (const s of suggestions) {
      const key = s.categoryId ?? "uncategorized";
      const existing = groupMap.get(key);
      if (existing) {
        existing.suggestions.push(s);
      } else {
        groupMap.set(key, {
          categoryId: key,
          categoryName: s.categoryName,
          suggestions: [s],
        });
      }
    }
    return [...groupMap.values()];
  }, [suggestions]);

  const toggleSelection = useCallback((id: Id<"checklistItems">) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleConfirm = async () => {
    const accepted = suggestions
      .filter((s) => selectedIds.has(s.checklistItemId))
      .map((s) => s.checklistItemId);
    const rejected = suggestions
      .filter((s) => !selectedIds.has(s.checklistItemId))
      .map((s) => s.checklistItemId);

    await acceptSuggestions(documentId, accepted, rejected);
    onComplete?.();
  };

  const handleDismiss = async () => {
    await dismissAll(documentId);
    onComplete?.();
  };

  if (suggestions.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase text-muted font-medium tracking-wider">
          Suggested checklist matches ({suggestions.length})
        </p>
        <button
          type="button"
          onClick={() => {
            if (selectedIds.size === suggestions.length) {
              setSelectedIds(new Set());
            } else {
              setSelectedIds(
                new Set(suggestions.map((s) => s.checklistItemId)),
              );
            }
          }}
          className="text-[11px] text-accent hover:text-accent/80 transition-colors cursor-pointer"
        >
          {selectedIds.size === suggestions.length
            ? "Deselect all"
            : "Select all"}
        </button>
      </div>

      <div className="space-y-4">
        {categoryGroups.map((group) => (
          <div key={group.categoryId}>
            <p className="text-[11px] font-medium text-secondary mb-1.5 px-0.5">
              {group.categoryName}
            </p>
            <div className="space-y-2">
              {group.suggestions.map((suggestion) => (
                <SuggestionCard
                  key={suggestion.checklistItemId}
                  suggestion={suggestion}
                  isSelected={selectedIds.has(suggestion.checklistItemId)}
                  onToggle={() => toggleSelection(suggestion.checklistItemId)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <Button
          onClick={handleConfirm}
          disabled={isAccepting || selectedIds.size === 0}
          className="flex-1"
        >
          {isAccepting ? (
            <Loader2 size={14} className="animate-spin mr-1.5" />
          ) : null}
          Confirm {selectedIds.size} selection
          {selectedIds.size !== 1 ? "s" : ""}
        </Button>
        <Button variant="ghost" onClick={handleDismiss} disabled={isAccepting}>
          Dismiss all
        </Button>
      </div>
    </div>
  );
}
